import { FormEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Plus,
  Trash2,
  MessageSquare,
  Copy,
  Check,
  Pencil,
  X,
  ArrowLeft,
  FolderKanban,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { Button, projectDisplayName } from "../ui";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";
import AskComposer, { AskLevel, useAskLevel } from "../components/AskComposer";
import { useResearchMode, type ResearchMode } from "../components/ResearchModePicker";
import { useDocAttachment } from "../components/useDocAttachment";
import AssistantMessage from "../components/AssistantMessage";
import {
  FigureSourceCard,
  TableSourceCard,
  TextSourceCard,
} from "../components/source-pane/ChatSourceCard";

interface SourceBase {
  document_id: string;
  document_title: string;
  score: number;
}

interface TextSource extends SourceBase {
  kind?: "text";
  chunk_id: string;
  ordinal: number;
  content: string;
}

interface FigureSource extends SourceBase {
  kind: "figure";
  fig_id: string;
  page: number;
  caption: string | null;
  source_hash?: string | null;
  external_source_slug?: string | null;
}

interface TableSource extends SourceBase {
  kind: "table";
  table_id: string;
  page: number;
  caption: string | null;
  summary: string | null;
  html_preview: string | null;
  source_hash?: string | null;
  external_source_slug?: string | null;
}

type Source = TextSource | FigureSource | TableSource;

function sourceKind(s: Source): "text" | "figure" | "table" {
  return (s.kind ?? "text") as "text" | "figure" | "table";
}

function sourceKey(s: Source, i: number): string {
  const k = sourceKind(s);
  if (k === "figure") return `fig:${(s as FigureSource).fig_id}:${i}`;
  if (k === "table") return `tbl:${(s as TableSource).table_id}:${i}`;
  return `txt:${(s as TextSource).chunk_id}:${i}`;
}

interface ChatRow {
  id: string;
  project_id: string | null;
  project_name: string | null;
  owner: string;
  title: string;
  model_override: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview: string | null;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sources_json: Source[] | null;
  model: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at: string;
}

interface ChatDetail {
  chat: ChatRow;
  messages: ChatMessage[];
}

// Level → Modell. Easy nutzt Server-Default (gemini-2.5-flash-lite synth +
// gemini-3.1-flash-lite plan). Der Level selbst liegt zentral in AskComposer
// (localStorage `sb_ask_level`, Default „Standard").
const LEVEL_MODELS: Record<AskLevel, string | null> = {
  easy: null,
  medium: "gemini-3-flash-preview",
  hard: "claude-sonnet-4-6",
  sophisticated: "claude-opus-4-7",
};

function fmtRelative(iso: string) {
  const d = new Date(iso);
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return "gerade";
  if (diffSec < 3600) return `vor ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 7) return `vor ${Math.floor(diffSec / 86400)} T`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

export default function ChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const modal = useModal();
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [projMenuOpen, setProjMenuOpen] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  // Anhang landet im Projekt des aktuellen Chats (bei neuem Chat: „Allgemein").
  const attach = useDocAttachment(detail?.chat.project_id ?? null);
  // Rename-State: { id, mode } — id ist die chatId, mode unterscheidet
  // Desktop-Inline ('header'/'sidebar') vs Mobile-Modal ('modal').
  const [renameTarget, setRenameTarget] = useState<
    { id: string; mode: "header" | "sidebar" | "modal" } | null
  >(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [level, setLevel] = useAskLevel();
  const [researchMode, setResearchMode] = useResearchMode();
  const pendingSentRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const activeModel: string | null = LEVEL_MODELS[level];

  // Sidebar-Scoping (Task 84c5251a): sobald ein Chat offen ist, listet die
  // Sidebar nur die Chats SEINES Projekts (Geschwister-Chats) statt global
  // aller. Ohne offenen Chat (/chat) bleibt die Liste global. Jeder Chat hat
  // ein Projekt (Default „Allgemein", Mig 0046) → am offenen Chat ist
  // project_id gesetzt.
  const scopeProjectId = detail?.chat.project_id ?? null;
  const scopeProjectName = detail?.chat.project_name ?? null;

  const loadChats = async (projectId: string | null) => {
    try {
      const url = projectId
        ? `/chats?project_id=${encodeURIComponent(projectId)}&limit=50`
        : "/chats?limit=50";
      setChats(await api.get<ChatRow[]>(url));
    } catch (e) {
      toast.err("Chats konnten nicht geladen werden", (e as Error).message);
    }
  };

  // Projekte einmalig laden (für die Projekt-Zuordnung im Header-Menü).
  useEffect(() => {
    api
      .get<{ id: string; name: string }[]>("/projects")
      .then(setProjects)
      .catch(() => {});
  }, []);

  // Chat-Liste (neu) laden, sobald sich der Scope ändert: kein offener Chat →
  // global; offener Chat → dessen Projekt.
  useEffect(() => {
    loadChats(scopeProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeProjectId]);

  useEffect(() => {
    if (!chatId) {
      setDetail(null);
      return;
    }
    (async () => {
      try {
        setDetail(await api.get<ChatDetail>(`/chats/${chatId}`));
      } catch (e) {
        toast.err("Chat konnte nicht geladen werden", (e as Error).message);
        navigate("/chat", { replace: true });
      }
    })();
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  // Home-Ask-Handoff: Overview legt den Chat an und navigiert sofort hierher
  // mit { state: { pendingQuestion } }. Sobald das Detail des Chats geladen ist,
  // senden wir die Frage über den normalen Sende-Flow (identisch zu einer manuell
  // getippten Nachricht) und räumen den History-State ab, damit ein Reload nicht
  // erneut sendet.
  useEffect(() => {
    const st = location.state as
      | { pendingQuestion?: string; pendingResearchMode?: ResearchMode }
      | null;
    const pending = st?.pendingQuestion;
    if (!pending || pendingSentRef.current) return;
    if (!chatId || !detail || detail.chat.id !== chatId) return;
    pendingSentRef.current = true;
    // Home-Composer kann eine Recherche-Tiefe mitgeben — in den Picker
    // übernehmen (sichtbar) und für diesen Send explizit durchreichen.
    const rm = st?.pendingResearchMode;
    if (rm) setResearchMode(rm);
    navigate(`/chat/${chatId}`, { replace: true, state: {} });
    void send(undefined, pending, rm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, chatId, detail]);

  const newChat = async () => {
    try {
      const c = await api.post<ChatRow>("/chats", {});
      await loadChats(c.project_id ?? null);
      navigate(`/chat/${c.id}`);
    } catch (e) {
      toast.err("Neuer Chat fehlgeschlagen", (e as Error).message);
    }
  };

  const startRename = (id: string, currentTitle: string, mode: "header" | "sidebar" | "modal") => {
    setRenameTarget({ id, mode });
    setRenameValue(currentTitle);
  };
  const cancelRename = () => {
    setRenameTarget(null);
    setRenameValue("");
  };
  const commitRename = async () => {
    if (!renameTarget) return;
    const t = renameValue.trim();
    if (!t) {
      cancelRename();
      return;
    }
    setRenameBusy(true);
    try {
      const updated = await api.patch<ChatRow>(`/chats/${renameTarget.id}`, { title: t });
      // Reflect updated title locally without full reload.
      setChats((prev) => prev.map((c) => (c.id === updated.id ? { ...c, title: updated.title } : c)));
      if (detail && detail.chat.id === updated.id) {
        setDetail({ ...detail, chat: { ...detail.chat, title: updated.title } });
      }
      cancelRename();
    } catch (e) {
      toast.err("Umbenennen fehlgeschlagen", (e as Error).message);
    } finally {
      setRenameBusy(false);
    }
  };

  // Chat nachträglich einem Projekt zuordnen (PATCH /chats/{id}).
  const assignProject = async (projectId: string, projectName: string) => {
    if (!detail) return;
    setProjMenuOpen(false);
    if (detail.chat.project_id === projectId) return;
    try {
      const updated = await api.patch<ChatRow>(`/chats/${detail.chat.id}`, {
        project_id: projectId,
      });
      setDetail({
        ...detail,
        chat: {
          ...detail.chat,
          project_id: updated.project_id,
          project_name: updated.project_name ?? projectName,
        },
      });
      setChats((prev) =>
        prev.map((c) =>
          c.id === updated.id
            ? { ...c, project_id: updated.project_id, project_name: updated.project_name }
            : c
        )
      );
      toast.ok("Projekt zugeordnet", projectName);
    } catch (e) {
      toast.err("Zuordnung fehlgeschlagen", (e as Error).message);
    }
  };

  const deleteChat = async (id: string) => {
    const ok = await modal.confirm({
      title: "Chat löschen?",
      description: "Verlauf geht verloren.",
      confirmLabel: "Löschen",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/chats/${id}`);
      if (id === chatId) navigate("/chat", { replace: true });
      // Aktiven Chat gelöscht → zurück auf global (der Scope-Effect zieht ohnehin
      // nach); sonst Geschwister-Liste im aktuellen Scope refreshen.
      await loadChats(id === chatId ? null : scopeProjectId);
    } catch (e) {
      toast.err("Löschen fehlgeschlagen", (e as Error).message);
    }
  };

  const send = async (
    e?: FormEvent,
    override?: string,
    researchModeOverride?: ResearchMode,
  ) => {
    e?.preventDefault();
    const text = (override ?? q).trim();
    if (!text) return;
    const effectiveResearchMode = researchModeOverride ?? researchMode;
    let activeId = chatId;
    if (!activeId) {
      try {
        // Bei angehängtem Dokument: neuen Chat im Projekt des Docs anlegen,
        // damit das Retrieval (chat.project_id-gescopet) es findet.
        const doc = attach.attachment?.doc ?? null;
        const c = await api.post<ChatRow>(
          "/chats",
          doc ? { project_id: doc.project_id } : {}
        );
        activeId = c.id;
        navigate(`/chat/${activeId}`, { replace: true });
      } catch (err) {
        toast.err("Chat-Erstellung fehlgeschlagen", (err as Error).message);
        return;
      }
    }
    setQ("");
    setBusy(true);
    // Optimistisch User-Message anzeigen
    setDetail((d) => {
      if (!d) return d;
      const optimistic: ChatMessage = {
        id: `tmp_${Date.now()}`,
        chat_id: d.chat.id,
        role: "user",
        content: text,
        sources_json: null,
        model: null,
        tokens_in: null,
        tokens_out: null,
        created_at: new Date().toISOString(),
      };
      return { ...d, messages: [...d.messages, optimistic] };
    });
    try {
      const res = await api.post<ChatDetail>(`/chats/${activeId}/messages`, {
        content: text,
        model: activeModel,
        top_k: 20,
        research_mode: effectiveResearchMode,
      });
      setDetail(res);
      attach.clear();
      await loadChats(res.chat.project_id ?? null);
    } catch (err) {
      toast.err("Antwort fehlgeschlagen", (err as Error).message);
      // Reload für sauberen State
      if (activeId) {
        try {
          setDetail(await api.get<ChatDetail>(`/chats/${activeId}`));
        } catch {
          /* swallow */
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] h-full min-h-0 overflow-hidden">
      {/* Chat-Liste — mobil genau eine Pane: Liste ODER Chat (§0.7). */}
      <aside
        className={clsx(
          "min-h-0 border-r border-cream-200 dark:border-ink-600 bg-cream-100/60 dark:bg-ink-800/60 flex-col",
          chatId ? "hidden md:flex" : "flex"
        )}
      >
        {/* Desktop: „Neuer Chat" oben. Mobil sitzt der Button fix unten über
            der Tab-Bar (besser erreichbar) — siehe unten. */}
        <div className="hidden md:block p-2 border-b border-border">
          <Button
            variant="primary"
            onClick={newChat}
            icon={<Plus className="w-4 h-4" />}
            className="w-full"
          >
            Neuer Chat
          </Button>
        </div>
        {/* Scope-Anzeige: sobald ein Chat offen ist, ist die Liste auf dessen
            Projekt gefiltert — „Alle Chats" hebt den Scope wieder auf (/chat). */}
        {scopeProjectId && (
          <div className="px-2 pt-2 flex items-center justify-between gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 text-text-2 min-w-0">
              <FolderKanban className="w-3 h-3 shrink-0" />
              <span className="truncate">{scopeProjectName ?? "Projekt"}</span>
            </span>
            <button
              onClick={() => navigate("/chat")}
              className="shrink-0 text-brand-600 hover:text-brand-700 hover:underline"
            >
              Alle Chats
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-1 py-1 pb-24 md:pb-1">
          {chats.length === 0 && (
            <div className="text-xs text-text-2 text-center py-6 px-3">
              Noch keine Chats. Klick „Neuer Chat" oder schreib unten direkt los.
            </div>
          )}
          {chats.map((c) => {
            const isRenaming = renameTarget?.id === c.id && renameTarget.mode === "sidebar";
            return (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => { if (!isRenaming) navigate(`/chat/${c.id}`); }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !isRenaming) {
                  e.preventDefault();
                  navigate(`/chat/${c.id}`);
                }
              }}
              className={clsx(
                "group w-full text-left px-2 py-1.5 rounded-md text-sm flex items-start gap-2 cursor-pointer",
                c.id === chatId
                  ? "bg-surface-3/70"
                  : "hover:bg-surface-3"
              )}
            >
              <MessageSquare className="w-3.5 h-3.5 text-text-2 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                {isRenaming ? (
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                      if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                    }}
                    autoFocus
                    disabled={renameBusy}
                    aria-label="Chat-Titel bearbeiten"
                    className="w-full px-1 py-0.5 rounded-md border border-brand-500 bg-surface dark:bg-ink-900 text-[13px] font-medium"
                  />
                ) : (
                  <div
                    className="truncate font-medium text-[13px]"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      startRename(c.id, c.title, "sidebar");
                    }}
                    title="Doppelklick zum Umbenennen"
                  >
                    {c.title}
                  </div>
                )}
                <div className="flex items-center gap-1 text-[11px] text-text-2">
                  {c.project_name && (
                    <span className="truncate max-w-[100px]">{projectDisplayName(c.project_name)}</span>
                  )}
                  <span>·</span>
                  <span>{fmtRelative(c.updated_at)}</span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startRename(c.id, c.title, "modal");
                }}
                className="md:hidden text-text-3 hover:text-brand-600 flex items-center justify-center shrink-0 min-h-11 min-w-11"
                title="Chat umbenennen"
                aria-label="Chat umbenennen"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteChat(c.id);
                }}
                className="md:opacity-0 md:group-hover:opacity-100 text-text-3 hover:text-danger flex items-center justify-center shrink-0 min-h-11 min-w-11 md:min-h-0 md:min-w-0 md:p-0.5"
                title="Löschen"
                aria-label="Chat löschen"
              >
                <Trash2 className="w-4 h-4 md:w-3.5 md:h-3.5" />
              </button>
            </div>
          );
          })}
        </div>
      </aside>

      {/* Mobile: fixer „Neuer Chat"-Button unten über der Tab-Bar (nur in der
          Listen-Ansicht, d. h. kein aktiver Chat). Safe-Area berücksichtigt. */}
      {!chatId && (
        <div
          className="md:hidden fixed inset-x-0 z-20 px-3"
          style={{ bottom: "calc(3.5rem + env(safe-area-inset-bottom) + 0.5rem)" }}
        >
          <Button
            variant="primary"
            onClick={newChat}
            icon={<Plus className="w-4 h-4" />}
            className="w-full shadow-lg"
          >
            Neuer Chat
          </Button>
        </div>
      )}

      {/* Chat — section nimmt volle Breite, Scroll-Container ist die
          messages-area (volle Breite → Scrollbar am Browser-Rand statt
          am Chat-Container). max-w-3xl gilt nur für den INNEREN
          Content-Wrapper (Header, Messages, Composer), nicht für die
          scrollable area selbst. */}
      <section
        className={clsx(
          "h-full min-h-0 flex-col",
          chatId ? "flex" : "hidden md:flex"
        )}
      >
        <header className="border-b border-cream-200/60 dark:border-ink-600/60">
          <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-3 flex items-end justify-between gap-2">
            <button
              onClick={() => navigate("/chat")}
              className="md:hidden -ml-1 mb-1 p-1 text-ink-50/60 hover:text-brand-600 shrink-0"
              title="Zurück zur Chat-Liste"
              aria-label="Zurück zur Chat-Liste"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-semibold flex items-center gap-2 min-w-0 truncate">
                <Sparkles className="w-6 h-6 text-brand-500 shrink-0" />
                {detail && renameTarget?.id === detail.chat.id && renameTarget.mode === "header" ? (
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                      if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                    }}
                    autoFocus
                    disabled={renameBusy}
                    aria-label="Chat-Titel bearbeiten"
                    className="flex-1 min-w-0 px-2 py-1 rounded-md border border-brand-500 bg-surface dark:bg-ink-900 text-2xl font-semibold"
                  />
                ) : (
                  <>
                    <span
                      className="truncate"
                      onDoubleClick={() => {
                        if (detail) startRename(detail.chat.id, detail.chat.title, "header");
                      }}
                      title="Doppelklick zum Umbenennen"
                    >
                      {detail?.chat.title ?? "Neuer Chat"}
                    </span>
                    {detail && (
                      <button
                        onClick={() => startRename(detail.chat.id, detail.chat.title, "modal")}
                        className="md:hidden text-ink-50/50 hover:text-brand-600 flex items-center justify-center shrink-0 min-h-11 min-w-11"
                        title="Chat umbenennen"
                        aria-label="Chat umbenennen"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </h1>
              {detail && (
                <div className="relative mt-1 inline-block">
                  <button
                    type="button"
                    onClick={() => setProjMenuOpen((o) => !o)}
                    className="inline-flex items-center gap-1 text-xs text-ink-50/60 hover:text-brand-600 transition-colors max-w-full"
                    title="Chat einem Projekt zuordnen"
                    aria-haspopup="menu"
                    aria-expanded={projMenuOpen}
                  >
                    <FolderKanban className="w-3 h-3 shrink-0" />
                    <span className="truncate">
                      Projekt: {projectDisplayName(detail.chat.project_name) || "Allgemein"}
                    </span>
                    <ChevronDown className="w-3 h-3 shrink-0" />
                  </button>
                  {projMenuOpen && (
                    <>
                      <button
                        type="button"
                        aria-label="Projektauswahl schließen"
                        className="fixed inset-0 z-30 cursor-default"
                        onClick={() => setProjMenuOpen(false)}
                      />
                      <div className="absolute z-40 top-full mt-1.5 left-0 w-56 max-h-72 overflow-y-auto bg-surface border border-border rounded-xl shadow-lg py-1">
                        {projects.length === 0 && (
                          <div className="px-3 py-2 text-xs text-text-3">
                            Keine Projekte
                          </div>
                        )}
                        {projects.map((p) => {
                          const active = detail.chat.project_id === p.id;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => assignProject(p.id, p.name)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-text hover:bg-surface-2 transition-colors"
                            >
                              <span className="flex-1 truncate">{projectDisplayName(p.name)}</span>
                              {active && (
                                <Check className="w-3.5 h-3.5 text-accent-600 shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        <div
          className={clsx(
            "flex-1 overflow-y-auto relative transition-colors",
            dropOver && "bg-accent-50/60 dark:bg-accent-900/10"
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDropOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node))
              setDropOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDropOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) attach.onFile(f);
          }}
        >
          {dropOver && (
            <div className="pointer-events-none absolute inset-3 z-10 rounded-2xl border-2 border-dashed border-accent-300 bg-accent-50/50 dark:bg-accent-900/20 grid place-items-center">
              <span className="text-sm font-medium text-accent-600">
                Dokument hier ablegen
              </span>
            </div>
          )}
          <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-8 space-y-6">
            {!detail || detail.messages.length === 0 ? (
              <div className="max-w-2xl mx-auto space-y-6">
                <StandaloneEmptyState onPick={(s) => {
                  setQ(s);
                  setTimeout(() => composerRef.current?.focus(), 0);
                }} />
                <AskComposer
                  q={q}
                  setQ={setQ}
                  onSend={send}
                  busy={busy}
                  composerRef={composerRef}
                  level={level}
                  setLevel={setLevel}
                  attach={attach}
                  researchMode={researchMode}
                  setResearchMode={setResearchMode}
                />
              </div>
            ) : (
              detail.messages.map((m) => <MessageBubble key={m.id} msg={m} />)
            )}
            {busy && (
              <div className="flex items-center gap-2 text-sm text-ink-50/60">
                <Loader2 className="w-4 h-4 animate-spin text-brand-500" /> denkt nach …
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {detail && detail.messages.length > 0 && (
          <div className="border-t border-cream-200 dark:border-ink-600">
           <div className="max-w-3xl mx-auto px-4 md:px-6 pb-6 pt-3">
            <AskComposer
              q={q}
              setQ={setQ}
              onSend={send}
              busy={busy}
              composerRef={composerRef}
              level={level}
              setLevel={setLevel}
              attach={attach}
              researchMode={researchMode}
              setResearchMode={setResearchMode}
            />
           </div>
          </div>
        )}
      </section>

      {/* Rename-Modal — Mobile-Modus (Pencil-Icon-Trigger).
          Desktop nutzt Inline-Input in Header/Sidebar via onDoubleClick. */}
      <Dialog.Root
        open={renameTarget?.mode === "modal"}
        onOpenChange={(o) => { if (!o) cancelRename(); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[420px] max-w-[92vw] p-5 animate-modal-in">
            <div className="flex items-center justify-between mb-3">
              <Dialog.Title className="font-semibold text-sm">Chat umbenennen</Dialog.Title>
              <button
                onClick={cancelRename}
                aria-label="Schliessen"
                className="text-text-3 hover:text-text min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center"
              >
                <X className="w-5 h-5 md:w-4 md:h-4" />
              </button>
            </div>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
              }}
              autoFocus
              disabled={renameBusy}
              aria-label="Chat-Titel"
              maxLength={200}
              className="w-full px-3 py-2 rounded-md border border-border-strong bg-surface dark:bg-ink-900 text-sm min-h-11 md:min-h-0"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={cancelRename}
                disabled={renameBusy}
                className="min-h-11 md:min-h-0"
              >
                Abbrechen
              </Button>
              <Button
                variant="primary"
                onClick={commitRename}
                loading={renameBusy}
                disabled={!renameValue.trim()}
                className="min-h-11 md:min-h-0"
              >
                Speichern
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function StandaloneEmptyState({ onPick }: { onPick: (q: string) => void }) {
  const suggestions = [
    "Wie wirkt Ectoin auf Hautzellen?",
    "Welche Dokumente habe ich zu klinischen Studien?",
    "Fasse meine neuesten Notizen zusammen.",
    "Was steht in der Wissensbasis zu medEctoin?",
  ];
  return (
    <div className="py-8 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 grid place-items-center shadow-lg shadow-brand-500/20">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <h2 className="font-display text-2xl text-ink tracking-tight">
          Frag <span className="italic text-brand-600 dark:text-brand-400">dein Wissen</span>
        </h2>
        <p className="text-sm text-ink-50/70 max-w-md mx-auto">
          Quellenbelegte Antwort auf Basis deiner Notizen + Dokumente.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="group text-left px-4 py-3 rounded-xl bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-sm transition"
          >
            <span className="text-[13.5px] text-ink leading-snug">
              {s}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const [openSources, setOpenSources] = useState(false);
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const srcs = msg.sources_json ?? [];
    const hasTopVisual = srcs.slice(0, 3).some(s => sourceKind(s) !== "text");
    if (hasTopVisual) {
      setOpenSources(true);
      autoOpenedRef.current = true;
    }
  }, [msg.sources_json, msg.id]);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (msg.role === "user") {
    const u =
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("sb_username")
        : null) ?? "?";
    return (
      <div className="group flex gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-cream-100/50 dark:hover:bg-ink-700/30 transition-colors">
        <div className="w-8 h-8 rounded-full bg-ink/85 dark:bg-cream-200 grid place-items-center text-cream-50 dark:text-ink text-xs font-semibold shrink-0">
          {u.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0 text-[15px] leading-relaxed whitespace-pre-wrap break-words text-ink pt-0.5">
          {msg.content}
        </div>
        <button
          onClick={handleCopy}
          title={copied ? "Kopiert!" : "Kopieren"}
          className="opacity-0 group-hover:opacity-100 self-start p-1 rounded-md text-ink-50/50 hover:text-ink hover:bg-cream-200 dark:hover:bg-ink-600 transition"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-brand-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    );
  }

  const sources = msg.sources_json ?? [];
  const [highlightSrc, setHighlightSrc] = useState<number | null>(null);

  // Inline-Citation [Quelle N] geklickt → Sources-Pane öffnen +
  // jeweilige Quelle hervorheben + dorthin scrollen.
  const handleCite = (n: number) => {
    setOpenSources(true);
    setHighlightSrc(n);
    setTimeout(() => {
      const el = document.getElementById(`src-${msg.id}-${n}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Sicherstellen, dass die details-Card aufgeklappt ist
        const det = el as HTMLDetailsElement;
        if (det.tagName === "DETAILS") det.open = true;
      }
      // Highlight nach 2.5s wieder weg
      setTimeout(() => setHighlightSrc(null), 2500);
    }, 50);
  };

  return (
    <div className="group flex gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-cream-100/30 dark:hover:bg-ink-700/20 transition-colors">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 grid place-items-center shrink-0 shadow-sm shadow-brand-500/20">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0 break-words">
        <AssistantMessage
          content={msg.content}
          onCite={sources.length > 0 ? handleCite : undefined}
          sources={sources}
        />
        <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-50/50">
          {msg.tokens_in != null && (
            <span className="font-mono">
              ⚡ {msg.tokens_in}+{msg.tokens_out ?? 0} tok
            </span>
          )}
        </div>
        {sources.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setOpenSources((v) => !v)}
              className="text-xs text-ink-50/60 hover:text-brand-600 dark:hover:text-brand-400 inline-flex items-center gap-1.5 transition-colors"
            >
              {openSources ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              <span>{sources.length} Quellen</span>
              {(() => {
                const figs = sources.filter(s => sourceKind(s) === "figure").length;
                const tbls = sources.filter(s => sourceKind(s) === "table").length;
                if (!figs && !tbls) return null;
                return (
                  <span className="text-ink-50/40">
                    ·{figs > 0 && <> {figs} Bild{figs === 1 ? "" : "er"}</>}
                    {figs > 0 && tbls > 0 && " ·"}
                    {tbls > 0 && <> {tbls} Tabelle{tbls === 1 ? "" : "n"}</>}
                  </span>
                );
              })()}
            </button>
            {openSources && (
              <div className="mt-2 space-y-2">
                {sources.map((s, i) => {
                  const n = i + 1;
                  const isHi = highlightSrc === n;
                  const k = sourceKind(s);
                  const defaultOpen = i < 3 && k !== "text";
                  const common = {
                    msgId: msg.id,
                    n,
                    isHi,
                    documentId: s.document_id,
                    documentTitle: s.document_title,
                    defaultOpen,
                  };
                  if (k === "figure") {
                    const f = s as FigureSource;
                    return (
                      <FigureSourceCard
                        key={sourceKey(s, i)}
                        {...common}
                        figId={f.fig_id}
                        page={f.page}
                        caption={f.caption}
                        sourceHash={f.source_hash ?? null}
                        externalSourceSlug={f.external_source_slug ?? null}
                      />
                    );
                  }
                  if (k === "table") {
                    const t = s as TableSource;
                    return (
                      <TableSourceCard
                        key={sourceKey(s, i)}
                        {...common}
                        page={t.page}
                        caption={t.caption}
                        summary={t.summary}
                        htmlPreview={t.html_preview}
                      />
                    );
                  }
                  const tx = s as TextSource;
                  return (
                    <TextSourceCard
                      key={sourceKey(s, i)}
                      {...common}
                      content={tx.content}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <button
        onClick={handleCopy}
        title={copied ? "Kopiert!" : "Antwort kopieren"}
        className="opacity-0 group-hover:opacity-100 self-start p-1 rounded-md text-ink-50/50 hover:text-ink hover:bg-cream-200 dark:hover:bg-ink-600 transition"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-brand-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
